import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import emailjs from 'emailjs-com';
import { useNavigate } from 'react-router-dom';
import { query, orderByChild, equalTo, get, child } from "firebase/database";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getDatabase, ref, set } from "firebase/database";
import { auth, db } from "../firebase/config";
import PhoneInput from "react-phone-input-2";
import meterVideo from "../assets/images/speed.gif";

import "react-phone-input-2/lib/style.css";
import Hatchback from "../assets/images/hatchbacks.png";
import SUV from "../assets/images/suvs.png";
import Coupe from "../assets/images/coupes.png";
import Sedan from "../assets/images/sedans.png";
import Minivan from "../assets/images/minivans.png";
import Truck from "../assets/images/trucks.png";
import CarLoan from "./CarLoans";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faPencil,
  faCheckCircle,
  faPen as faPencilAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useRef } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";

type VehicleType =
  | "Coupe"
  | "Hatchback"
  | "Sedan"
  | "Minivan"
  | "SUV & Crossover"
  | "Truck";

const vehicleImages: Record<VehicleType, string> = {
  Coupe,
  Hatchback,
  Sedan,
  Minivan,
  "SUV & Crossover": SUV,
  Truck,
};

type CurrencyInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
};

const TOTAL_STEPS = 17;

const CarLoanForm: React.FC = () => {
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const libraries = ["places"] as const;
  const [showError, setShowError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleNext = () => {
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFullBack = () => {
    setStep(1);
  };
  const handleChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const [timer, setTimer] = useState(60); // 60 seconds
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  const checkIfUserExists = async (phone: string): Promise<boolean> => {
    const dbRef = ref(db);
    const snapshot = await get(child(dbRef, "users"));

    if (snapshot.exists()) {
      const users = snapshot.val();
      for (const uid in users) {
        if (users[uid].phoneNumber === phone) {
          return true;
        }
      }
    }
    return false;
  };

  const [processing2, setProcessing2] = useState(false); // 60 seconds


  const startPhoneAuth = async () => {
    setProcessing2(true)
    if (!auth) {
      console.error("Firebase auth not initialized");
      return;
    }

    const exists = await checkIfUserExists(phoneNumber);
    if (exists) {
      toast.error("User already exists with this phone number.");
      return;
    }

    try {
      // Destroy previous verifier if it exists
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }

      // Recreate the verifier
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response: any) => {},
        }
      );

      await recaptchaVerifierRef.current.render();

      const result = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifierRef.current
      );
      setConfirmationResult(result);
      setTimer(60); // reset timer
      setIsTimerRunning(true); // start timer
      setProcessing2(false)

      handleNext(); // only if you're calling this from step 16
    } catch (error: any) {
      console.error("Error during phone authentication:", error);
      if (error.code === "auth/too-many-requests") {
        toast.error(
          "Too many requests. Please try again later or use a test number."
        );
      } else {
        toast.error("Failed to send code. Please refresh and try again.");
      }
    }
  };

  useEffect(() => {
    let interval: any;

    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      clearInterval(interval);
      setIsTimerRunning(false);
    }

    return () => clearInterval(interval);
  }, [isTimerRunning, timer]);

  const [processing, setProcessing] = useState(false); // 60 seconds
  const [showSubmitted, setSubmitted] = useState(false);


  const verifyCode = async () => {
    if (confirmationResult) {
      try {
        setProcessing(true)
        const userCredential = await confirmationResult.confirm(verificationCode);
        const userId = userCredential.user.uid;
  
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        
        formData.date = formattedDate;
        
        await set(ref(db, "users/" + userId), formData);
          
        // Select only the needed fields
        const selectedData = {
          vehicleType: formData.vehicleType,
          budget: formData.budget,
          tradeIn: formData.tradeIn,
          creditRating: formData.creditRating,
          employmentStatus: formData.employmentStatus,
          incomeType: formData.incomeType,
          annualIncome: formData.annualIncome,
          hourlyWage: formData.hourlyWage,
          hoursPerWeek: formData.hoursPerWeek,
          monthlyIncome: formData.monthlyIncome,
          incomeMonths: formData.incomeMonths,
          incomeYears: formData.incomeYears,
          companyName: formData.companyName,
          jobTitle: formData.jobTitle,
          address: formData.address,
          addressMonths: formData.addressMonths,
          addressYears: formData.addressYears,
          housingStatus: formData.housingStatus,
          monthlyPayment: formData.monthlyPayment,
          citizenship: formData.citizenship,
          driversLicence: formData.driversLicence,
          dob: formData.dob,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber
        };
  
        // Send selectedData via EmailJS
        await emailjs.send(
          'service_644184m',
          'template_b00dwni',
          selectedData,
          'IaQJVz7G8oUHlN1_1'
        );
  

        // await emailjs.send(
        //   'service_644184m',
        //   'template_uo6mfnk',
        //   {
        //     firstName: formData.firstName,
        //     email: formData.email,
        //     currentYear: new Date().getFullYear()
        //   },
        //   'IaQJVz7G8oUHlN1_1'
        // );
        setProcessing(false)
        setSubmitted(true)
        
      } catch (error) {
        console.error("Verification or Email Failed:", error);
        toast.error("An error occurred");
      }
    }
  };
  

  const [dobError, setDobError] = useState({ format: "", future: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [inputHome, setInputHome] = useState(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBbLZT7PhPsNbjjTk0slK39iiThJf4pfUk",
    libraries: ["places"],
    language: "en", // Ensure the language is English
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [predictions, setPredictions] = useState<
    google.maps.places.QueryAutocompletePrediction[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      let address = place?.formatted_address || "";

      // Remove "Canada" and any commas from the formatted address
      address = address.replace(/,?\s*Canada$/, "").replace(/,/g, "");

      setSelectedAddress(address);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setInputHome(false);
    const service = new google.maps.places.AutocompleteService();

    // Cast the request to allow componentRestrictions
    const request = {
      input: value,
      componentRestrictions: { country: "ca" },
    } as google.maps.places.QueryAutocompletionRequest;

    service.getQueryPredictions(request, displaySuggestions);
  };

  const [city, setCity] = useState<string>("");

  const displaySuggestions = (
    predictions: google.maps.places.QueryAutocompletePrediction[] | null,
    status: google.maps.places.PlacesServiceStatus
  ) => {
    if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
      setPredictions([]);
      return;
    }

    // Modify the predictions to remove "Canada" and commas
    const modifiedPredictions = predictions.map((prediction) => {
      let modifiedDescription = prediction.description;

      // Remove "Canada" and any commas from the description
      modifiedDescription = modifiedDescription.replace(/,?\s*Canada$/, "");

      return { ...prediction, description: modifiedDescription };
    });

    setPredictions(modifiedPredictions);
  };

  const handleSelectAddress = (address: string) => {
    setInputValue(address);
    setPredictions([]); // Clear suggestions
    setSelectedAddress(address);
    setInputHome(true);
    handleChange("address", address);

    // Extract city from address (assuming it's in the format: "Street, City, Province")
    const parts = address.split(",");
    if (parts.length >= 2) {
      const extractedCity = parts[1].trim(); // Take the second part
      setCity(extractedCity);
    }
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const [error, setError] = useState("");

  const handleSendCode = () => {
    const email = formData.email;
    if (!email) {
      showErrorss("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      showErrorss("Please enter a valid email address");
      return;
    }

    startPhoneAuth();
  };

  const showErrorss = (message: any) => {
    setError(message);
    setTimeout(() => {
      setError("");
    }, 3000);
  };

  const otpRefs = useRef<React.RefObject<HTMLInputElement>[]>([]);

  // Initialize refs
  if (otpRefs.current.length === 0) {
    otpRefs.current = Array(6)
      .fill(null)
      .map(() => React.createRef<HTMLInputElement>());
  }

  // Handle input change
  const handleOTPChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value.replace(/\D/, ""); // only digits
    const codeArray = verificationCode.split("").slice(0, 6); // existing code split
    codeArray[index] = value;
    const newCode = codeArray.join("");
    setVerificationCode(newCode);

    // Move to next box if current filled
    if (value && index < 5) {
      otpRefs.current[index + 1].current?.focus();
    }
  };

  // Handle backspace navigation
  const handleOTPKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && index > 0 && !verificationCode[index]) {
      otpRefs.current[index - 1].current?.focus();
    }
  };

  const [showPopup, setShowPopup] = useState(false);
  const [showPreApproval, setShowPreApproval] = useState(false);

  const navigate = useNavigate();

  const handleNextPopups = () => {
    navigate('/'); // this goes to the homepage
  };

  const handleNextClick = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
      setShowPreApproval(true);
    }, 3000); // Wait for 5 seconds
  };

  const handleNextPopup = () => {
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
    setShowPreApproval(false);
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        const vehicleOptions = [
          { type: "Coupe", img: Coupe },
          { type: "Hatchback", img: Hatchback },
          { type: "Sedan", img: Sedan },
          { type: "Minivan", img: Minivan },
          { type: "SUV & Crossover", img: SUV },
          { type: "Truck", img: Truck },
        ];

        return (
          <>
            <h2
              style={{
                fontSize: 22,
                margin: "10px 0",
                textAlign: "center",
                color: "#111",
              }}
            >
              Get Pre-Approved for the Car You Want at a Price You Can Afford
            </h2>
            <h3 style={{ fontSize: 14, textAlign: "center", color: "#111" }}>
              What type of vehicle are you looking for?
            </h3>
            <VehicleGrid>
              {vehicleOptions.map(({ type, img }) => (
                <VehicleCard
                  key={type}
                  selected={formData.vehicleType === type}
                  onClick={() => {
                    handleChange("vehicleType", type);
                    handleNext();
                  }}
                >
                  <img src={img} alt={type} />
                  <span>{type}</span>
                </VehicleCard>
              ))}
            </VehicleGrid>
          </>
        );

      case 2:
        const budgets = [
          "Under $400 / Month",
          "$400 - 499 / Month",
          "$500 - 600 / Month",
          "Over $600 / Month",
        ];

        return (
          <>
            <h3>What is your budget?</h3>
            <VehicleGrid2>
              {budgets.map((budget) => (
                <SelectableCard
                  key={budget}
                  selected={formData.budget === budget}
                  onClick={() => {
                    handleChange("budget", budget);
                    handleNext();
                  }}
                >
                  {budget}
                </SelectableCard>
              ))}
            </VehicleGrid2>
            <BackButton onClick={handleBack}>Back</BackButton>
          </>
        );

      case 3:
        const tradeOptions = ["Yes", "No", "Unsure"];
        return (
          <>
            <h3>Do you have a trade-in?</h3>
            <VehicleGrid>
              {tradeOptions.map((option) => (
                <SelectableCard
                  key={option}
                  selected={formData.tradeIn === option}
                  onClick={() => {
                    handleChange("tradeIn", option);
                    handleNext();
                  }}
                >
                  {option}
                </SelectableCard>
              ))}
            </VehicleGrid>
            <BackButton onClick={handleBack}>Back</BackButton>
          </>
        );

      case 4:
        const creditRatings = [
          "Excellent (760-900)",
          "Very Good (725-759)",
          "Good (660-724)",
          "Fair (600-659)",
          "Poor (300-599)",
          "No Credit / Unsure",
        ];
        return (
          <>
            <h3>What is your estimated credit rating?</h3>
            <VehicleGrid2>
              {creditRatings.map((rating) => (
                <SelectableCard
                  key={rating}
                  selected={formData.creditRating === rating}
                  onClick={() => {
                    handleChange("creditRating", rating);
                    handleNext();
                  }}
                >
                  {rating}
                </SelectableCard>
              ))}
            </VehicleGrid2>
            <BackButton onClick={handleBack}>Back</BackButton>
          </>
        );

      case 5:
        const employmentStatuses = [
          "Employed",
          "Self-Employed",
          "Student",
          "Retired / Pension",
          "Other",
        ];
        return (
          <>
            <h3>What is your employment status?</h3>
            <VehicleGrid2>
              {employmentStatuses.map((status) => (
                <SelectableCard
                  key={status}
                  selected={formData.employmentStatus === status}
                  onClick={() => {
                    handleChange("employmentStatus", status);
                    handleNext();
                  }}
                >
                  {status}
                </SelectableCard>
              ))}
            </VehicleGrid2>
            <BackButton onClick={handleBack}>Back</BackButton>
          </>
        );

      case 6:
        const incomeOptions = [
          "I Know My Annual Salary",
          "I Know My Hourly Wage",
          "I Know My Monthly Income",
          "Other",
        ];
        return (
          <>
            <h3>Income Details. Please Select One:</h3>
            <VehicleGrid2>
              {incomeOptions.map((incomeType) => (
                <SelectableCard
                  key={incomeType}
                  selected={formData.incomeType === incomeType}
                  onClick={() => {
                    handleChange("incomeType", incomeType);
                    handleNext();
                  }}
                >
                  {incomeType}
                </SelectableCard>
              ))}
            </VehicleGrid2>
            <BackButton onClick={handleBack}>Back</BackButton>
          </>
        );

      case 7:
        const incomeType = formData.incomeType;

        const isSalaryValid =
          incomeType === "I Know My Annual Salary" && formData.annualIncome;
        const isHourlyValid =
          incomeType === "I Know My Hourly Wage" &&
          formData.hourlyWage &&
          formData.hoursPerWeek;
        const isMonthlyValid =
          (incomeType === "I Know My Monthly Income" ||
            incomeType === "Other") &&
          formData.monthlyIncome;

        const isFormValid = isSalaryValid || isHourlyValid || isMonthlyValid;

        const CurrencyInput: React.FC<CurrencyInputProps> = ({
          value,
          onChange,
          placeholder,
        }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Input
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              style={{ flex: 1 }}
            />
          </div>
        );

        return (
          <>
            {incomeType === "I Know My Annual Salary" && (
              <>
                <h3>What is your annual income before taxes & deductions?</h3>
                <Input
                  placeholder="Annual Income ($)"
                  value={formData.annualIncome || ""}
                  onChange={(e) => handleChange("annualIncome", e.target.value)}
                />
              </>
            )}

            {incomeType === "I Know My Hourly Wage" && (
              <>
                <h3>How much do you get paid per hour?</h3>
                <Input
                  placeholder="Hourly Wage ($)"
                  value={formData.hourlyWage || ""}
                  onChange={(e) => handleChange("hourlyWage", e.target.value)}
                />
                <h3>Average Hours per Week</h3>
                <Input
                  placeholder="Average Hours per Week"
                  value={formData.hoursPerWeek || ""}
                  onChange={(e) => handleChange("hoursPerWeek", e.target.value)}
                />
              </>
            )}

            {(incomeType === "I Know My Monthly Income" ||
              incomeType === "Other") && (
              <>
                <h3>What is your monthly income before taxes & deductions?</h3>
                <Input
                  placeholder="Monthly Income ($)"
                  value={formData.monthlyIncome || ""}
                  onChange={(e) =>
                    handleChange("monthlyIncome", e.target.value)
                  }
                />
              </>
            )}

            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button onClick={handleNext} disabled={!isFormValid}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 8:
        const handleContinue = () => {
          const months = parseInt(formData.incomeMonths, 10);
          const years = parseInt(formData.incomeYears, 10);

          if (formData.incomeMonths && (isNaN(months) || months > 12)) {
            setErrorMessage("Months cannot be more than 12");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
          }

          if (formData.incomeYears && (isNaN(years) || years > 100)) {
            setShowError("Years cannot be more than 100");
            setTimeout(() => setShowError(""), 3000);
            return;
          }

          handleNext();
        };

        const isContinueDisabled =
          !formData.incomeYears ||
          !formData.incomeMonths ||
          (formData.incomeYears && !formData.incomeMonths) ||
          (!formData.incomeYears && formData.incomeMonths);
        return (
          <>
            <h3>How long have you been receiving this income?</h3>
            <Input
              placeholder="Years"
              value={formData.incomeYears || ""}
              onChange={(e) => handleChange("incomeYears", e.target.value)}
            />
            {showError && <Error>{showError}</Error>}

            <Input
              placeholder="Months"
              value={formData.incomeMonths || ""}
              onChange={(e) => handleChange("incomeMonths", e.target.value)}
            />
            {errorMessage && <Error>{errorMessage}</Error>}
            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button onClick={handleContinue} disabled={isContinueDisabled}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 9:
        const isContinueDisableds =
          !formData.companyName ||
          !formData.jobTitle ||
          (formData.companyName && !formData.jobTitle) ||
          (!formData.companyName && formData.jobTitle);

        return (
          <>
            <h3>Where do you work?</h3>
            <Input
              placeholder="Company Name"
              value={formData.companyName || ""}
              onChange={(e) => handleChange("companyName", e.target.value)}
            />
            <Input
              placeholder="Job Title"
              value={formData.jobTitle || ""}
              onChange={(e) => handleChange("jobTitle", e.target.value)}
            />
            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button onClick={handleNext} disabled={isContinueDisableds}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 10:
        return (
          <>
            <>
              <h3>Where are you looking for a vehicle?</h3>
              {isLoaded && (
                <AutocompleteContainer>
                  <Input
                    placeholder="Enter your address"
                    value={inputValue}
                    onChange={handleInputChange}
                  />
                  {inputHome && (
                    <div
                      style={{
                        background: "#ffeaea",
                        border: "2px solid #e61717",
                        padding: 15,
                        textAlign: "center",
                        borderRadius: 8,
                        color: "rgb(181,0,0)",
                      }}
                    >
                      <span style={{ color: "black", fontSize: 13 }}>
                        Your Selected Address is :<br />
                      </span>
                      {inputValue}
                    </div>
                  )}

                  {predictions.length > 0 && (
                    <Dropdown>
                      {predictions.map((prediction) => (
                        <DropdownItem
                          key={prediction.place_id}
                          onClick={() =>
                            handleSelectAddress(prediction.description)
                          }
                        >
                          {prediction.description}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  )}
                </AutocompleteContainer>
              )}
              <ButtonBlock>
                <BackButton onClick={() => handleBack()}>Back</BackButton>
                <Button onClick={handleNextClick} disabled={!formData.address}>
                  Continue
                </Button>
              </ButtonBlock>
            </>

            {/* Speed Meter Popup */}
            {showPopup && (
              <div className="popup-overlay">
                <div className="popup-content">
                  <img
                    src={meterVideo}
                    style={{
                      width: "200px",
                      transform: "rotate(-3deg)",
                    }}
                  />
                  <h3>Searching for Vehicles in: </h3>
                  <h4 className="stateCard">{city}</h4>
                </div>
              </div>
            )}

            {/* Pre-Approval Content */}
            {showPreApproval && (
              <div className="popup-overlay">
                <div className="popup-content">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    style={{ fontSize: 50, color: "#e61717" }}
                  />{" "}
                  <h2>Congratulations!</h2>
                  <p style={{ margin: 10 }}>You Qualify for Pre-Approval in:</p>
                  <h4 className="stateCard">{city}</h4>
                  <p style={{ margin: 10 }}>
                    By clicking the Continue button, I confirm that I agree to
                    Go Drive Auto’s{" "}
                    <a
                      href="https://godriveauto.ca/terms-of-use"
                      style={{ color: "black" }}
                    >
                      Terms of Use
                    </a>{" "}
                    . I also consent to Go Drive Auto and its affiliated
                    companies recording my calls for quality assurance and
                    training purposes. Additionally, I authorize Go Drive Auto,
                    partnered dealerships, and lenders to access my credit
                    report to help process my car loan application.
                  </p>
                  <Button onClick={handleNextPopup}>Continue</Button>
                </div>
              </div>
            )}
          </>
        );

      case 11:
        const handleAddressContinue = () => {
          const months = parseInt(formData.addressMonths, 10);
          const years = parseInt(formData.addressYears, 10);

          if (formData.addressMonths && (isNaN(months) || months > 12)) {
            setErrorMessage("Months cannot be more than 12");
            setTimeout(() => setErrorMessage(""), 3000);
            return;
          }

          if (formData.addressYears && (isNaN(years) || years > 100)) {
            setShowError("Years cannot be more than 100");
            setTimeout(() => setShowError(""), 3000);
            return;
          }

          handleNext();
        };

        const isContinueDisabledd =
          !formData.addressYears ||
          !formData.addressMonths ||
          (formData.addressYears && !formData.addressMonths) ||
          (!formData.addressYears && formData.addressMonths);
        return (
          <>
            <div
              style={{
                background: "#ffeaea",
                border: "2px solid #e61717",
                padding: 15,
                textAlign: "center",
                borderRadius: 8,
                color: "rgb(181,0,0)",
                margin: "10px 0",
              }}
            >
              <span style={{ color: "black", fontSize: 13 }}>
                Your Selected Address is :<br />
              </span>
              {formData.address || "Not provided yet"}
            </div>

            <h3>How long have you lived at your current address?</h3>
            <Input
              placeholder="Years"
              value={formData.addressYears || ""}
              onChange={(e) => handleChange("addressYears", e.target.value)}
            />
            {showError && <Error>{showError}</Error>}

            <Input
              placeholder="Months"
              value={formData.addressMonths || ""}
              onChange={(e) => handleChange("addressMonths", e.target.value)}
            />
            {errorMessage && <Error>{errorMessage}</Error>}
            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button
                onClick={handleAddressContinue}
                disabled={isContinueDisabledd}
              >
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 12:
        const isContinue =
          !formData.housingStatus ||
          !formData.monthlyPayment ||
          (formData.housingStatus && !formData.monthlyPayment) ||
          (!formData.housingStatus && formData.monthlyPayment);
        return (
          <>
            <h3>Do you rent or own your home?</h3>
            <VehicleGrid2>
              {["Rent", "Own"].map((option) => (
                <SelectableCard
                  key={option}
                  selected={formData.housingStatus === option}
                  onClick={() => {
                    handleChange("housingStatus", option);
                  }}
                >
                  {option}
                </SelectableCard>
              ))}
            </VehicleGrid2>

            <h3 style={{ marginTop: 20 }}>Monthly Payment</h3>
            <Input
              placeholder="Monthly Rent/Mortgage ($)"
              value={formData.monthlyPayment || ""}
              onChange={(e) => handleChange("monthlyPayment", e.target.value)}
            />
            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>{" "}
              <Button onClick={handleNext} disabled={isContinue}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 13:
        const isContinued =
          !formData.citizenship ||
          !formData.driversLicence ||
          (formData.citizenship && !formData.driversLicence) ||
          (!formData.citizenship && formData.driversLicence);
        return (
          <>
            <h3>Are you a Canadian citizen or permanent resident?</h3>
            <VehicleGrid2>
              {["Yes", "No"].map((option) => (
                <SelectableCard
                  key={option}
                  selected={formData.citizenship === option}
                  onClick={() => handleChange("citizenship", option)}
                >
                  {option}
                </SelectableCard>
              ))}
            </VehicleGrid2>

            <h3 style={{ marginTop: 20 }}>
              Do you have a valid Canadian driver's licence?
            </h3>
            <VehicleGrid2>
              {["Yes", "No"].map((option) => (
                <SelectableCard
                  key={`${option}-dl`}
                  selected={formData.driversLicence === option}
                  onClick={() => {
                    handleChange("driversLicence", option);
                  }}
                >
                  {option}
                </SelectableCard>
              ))}
            </VehicleGrid2>

            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>{" "}
              <Button onClick={handleNext} disabled={isContinued}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 14:
        const formatDOBInput = (value: string) => {
          const digits = value.replace(/\D/g, "");
          if (digits.length <= 2) {
            return digits;
          } else if (digits.length <= 4) {
            return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
          } else {
            return `${digits.slice(0, 2)} / ${digits.slice(
              2,
              4
            )} / ${digits.slice(4, 8)}`;
          }
        };

        const dobValue = formData.dob || "";

        const handleDOBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const formatted = formatDOBInput(e.target.value);
          handleChange("dob", formatted);
          setDobError({ format: "", future: "" });
          setShowErrors(false);
        };

        const handleDOBContinue = () => {
          if (!dobValue.trim()) return; // prevent continue if empty

          const regex =
            /^(0[1-9]|[12][0-9]|3[01]) \/ (0[1-9]|1[0-2]) \/ (19|20)\d\d$/;
          const formatValid = regex.test(dobValue);
          const parts = dobValue.split(" / ");
          const year = parseInt(parts[2]);
          const currentYear = new Date().getFullYear();
          const yearValid = !isNaN(year) && year <= currentYear;

          const formatError = formatValid ? "" : "Please enter correct DOB";
          const futureError =
            !formatValid || yearValid ? "" : "Year cannot be in the future";

          const hasError = formatError || futureError;

          if (hasError) {
            setDobError({ format: formatError, future: futureError });
            setShowErrors(true);

            // Hide after 3 seconds
            setTimeout(() => {
              setShowErrors(false);
            }, 3000000);
          } else {
            handleNext();
          }
        };

        return (
          <>
            <h3>What is your date of birth?</h3>
            <Input
              placeholder="DD / MM / YYYY"
              value={dobValue}
              onChange={handleDOBChange}
              maxLength={14}
            />
            {showErrors && dobError.format && <Error>{dobError.format}</Error>}
            {showErrors && dobError.future && <Error>{dobError.future}</Error>}

            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button onClick={handleDOBContinue} disabled={!dobValue.trim()}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 15:
        return (
          <>
            <h3>Almost done! What is your name?</h3>
            <Input
              placeholder="First Name"
              value={formData.firstName || ""}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
            <Input
              placeholder="Last Name"
              value={formData.lastName || ""}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />

            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>{" "}
              <Button onClick={handleNext} disabled={!formData.firstName || !formData.lastName}>
                Continue
              </Button>
            </ButtonBlock>
          </>
        );

      case 16:
        return (
          <>
            <h3>Let's Finalize the Application!</h3>
            <Input
              placeholder="Email Address"
              value={formData.email || ""}
              onChange={(e) => handleChange("email", e.target.value)}
            />

            {error && <Error>{error}</Error>}

            <p style={{ marginBottom: 20, textAlign: "center" }}>
              Please take a moment to verify your phone number. This helps us
              confirm your identity and secure your account. When you tap "Send
              Code," we will text you a code. Message and data rates may apply.
            </p>

            <PhoneInput
              country={"ca"}
              value={phoneNumber}
              onChange={(value: string) => {
                const phoneWithPlus = "+" + value;
                setPhoneNumber(phoneWithPlus);
                handleChange("phoneNumber", phoneWithPlus);
              }}
              inputStyle={{
                width: "100%",
                border: "1px solid #ddd",
                marginTop: 10,
                height: 50,
                padding: "10px 14px",
                paddingLeft: 50,
                borderRadius: "8px",
                fontSize: "16px",
              }}
            />

            <div id="recaptcha-container"></div>

            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button onClick={handleSendCode} 
               disabled={processing2}
                className={processing ? "opacity-50 cursor-not-allowed" : ""}
              >
                {processing2 ? "Sending..." : "Send Code"}</Button>            </ButtonBlock>
          </>
        );

      case 17:
        const formatPhoneNumber = (number: string) => {
          // Remove all non-numeric characters, except for the leading '+'
          const cleaned = ("" + number).replace(/\D/g, "");

          // Check if the number starts with country code +1
          if (cleaned.startsWith("1") && cleaned.length === 11) {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(
              4,
              7
            )}-${cleaned.slice(7)}`;
          }

          // If the number doesn't match, return the original number
          return number;
        };

        return (
          <>
            <Title>Enter the verification code</Title>
            <PhoneNumber>
              {formatPhoneNumber(phoneNumber)}{" "}
              <EditText onClick={handleBack}>
                <FontAwesomeIcon
                  icon={faPencilAlt}
                  style={{
                    cursor: "pointer",
                    color: "white",
                    background: "#e61717",
                    padding: 5,
                    marginTop: 2,
                    borderRadius: "50%",
                    fontSize: 10,
                  }}
                />
              </EditText>
            </PhoneNumber>
            <div id="recaptcha-container" />

            <OTPWrapper>
              {[...Array(6)].map((_, index) => (
                <OTPInput
                  key={index}
                  maxLength={1}
                  value={verificationCode[index] || ""}
                  onChange={(e) => handleOTPChange(e, index)}
                  ref={otpRefs.current[index]}
                  onKeyDown={(e) => handleOTPKeyDown(e, index)}
                />
              ))}
            </OTPWrapper>

            <ResendWrapper>
              {isTimerRunning ? (
                <>
                  <TimerText>
                    {String(Math.floor(timer / 60)).padStart(2, "0")}:
                    {String(timer % 60).padStart(2, "0")}
                  </TimerText>
                  <ResendText disabled>Resend OTP</ResendText>
                </>
              ) : (
                <ResendText onClick={startPhoneAuth}>Resend OTP</ResendText>
              )}
            </ResendWrapper>

            <ButtonBlock>
              <BackButton onClick={handleBack}>Back</BackButton>
              <Button 
  onClick={verifyCode} 
  disabled={processing}
  className={processing ? 'opacity-50 cursor-not-allowed' : ''}
>
  {processing ? 'Processing...' : 'Submit'}
</Button>
            </ButtonBlock>


            {showSubmitted && (
              <div className="popup-overlay">
                <div className="popup-content">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    style={{ fontSize: 50, color: "#e61717" }}
                  />{" "}
                  <h2>Congratulations!</h2>
                  <p style={{ margin: 10,fontSize:15 }}>Your Application has been Sumitted Sucessfully!<br/><br/>You will recieve confirmtion email shortly.</p>
                 
                  <Button onClick={handleNextPopups}>Back to Homepage</Button>
                </div>
              </div>
            )}
          </>
        );

      default:
        return <div>Thank you for completing your application!</div>;
    }
  };

  const progressPercentage = (step / TOTAL_STEPS) * 100;

  let estimatedTime = "3 minutes";
  if (progressPercentage >= 66) {
    estimatedTime = "1 minute";
  } else if (progressPercentage >= 33) {
    estimatedTime = "2 minutes";
  }

  const vehicleImg = vehicleImages[formData.vehicleType as VehicleType];

  return (
    <>
      <CarLoan />
      <Container>
        {step <= 2 ? (
          <>
            <div style={{ border: "1px solid #ddd", padding: "20px 10px" }}>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#6c757d",
                  textAlign: "center",
                }}
              >
                Estimated time to complete:
                <br />
                {estimatedTime}
              </p>
            </div>{" "}
            <ProgressBar progress={progressPercentage} />
          </>
        ) : (
          <div style={{ textAlign: "center", position: "relative" }}>
            <div style={{ border: "1px solid #ddd" }}>
              <div
                style={{
                  textAlign: "left",
                  display: "flex",
                  padding: "20px 10px",
                  gap: 20,
                }}
              >
                {vehicleImg && (
                  <img
                    src={vehicleImg}
                    alt={formData.vehicleType}
                    style={{ width: "100px", marginBottom: "0.5rem" }}
                  />
                )}
                <div>
                  {" "}
                  <p
                    style={{
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color: "#555",
                    }}
                  >
                    {formData.vehicleType}
                  </p>
                  <p>{formData.budget}</p>
                </div>

                <FontAwesomeIcon
                  onClick={handleFullBack}
                  icon={faPencilAlt}
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    color: "white",
                    background: "#e61717",
                    padding: 10,
                    borderRadius: "50%",
                    fontSize: 12,
                    right: 10,
                    top: 10,
                  }}
                />
              </div>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#6c757d",
                  marginBottom: 10,
                  marginTop: -5,
                }}
              >
                Estimated time to complete : &nbsp;
                {estimatedTime}
              </p>
            </div>
            <ProgressBar2 progress={progressPercentage} />
          </div>
        )}

        {renderStep()}
        <ToastContainer />
      </Container>
    </>
  );
};

export default CarLoanForm;

const BackButton = styled.button`
  display: block;
  width: 100%;

  margin-top: 1rem;
  padding: 0.75rem;
  font-size: 1rem;
  border: none;
  background: #ddd;

  color: black;
  font-family: var(--font-normal);

  border-radius: 4px;
  cursor: pointer;
`;

const VehicleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 1rem;
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns:1fr 1fr;
  }
`;

const ButtonBlock = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 2fr;
  margin-top: 10px;

  gap: 10px;
`;

const VehicleGrid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 1rem;
  gap: 10px;
`;

const VehicleCard = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  font-family: var(--font-normal);

  align-items: center;
  background: ${({ selected }) => (selected ? "#ffeaea" : "#f8f9fa")};
  border: ${({ selected }) =>
    selected ? "1px solid #e61717" : "1px solid #ddd"};
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  img {
    width: 100px;
    height: 60px;
    object-fit: contain;
  }

  span {
    font-weight: 500;
    color: #333;
  }

  &:hover {
    border-color: #e61717;
    background-color: #ffeaea;
  }
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 2rem;
  font-family: var(--font-normal);
  padding-top: 150px;
  padding-bottom: 100px;

  h3 {
    text-align: center;
    margin: 20px 0;
    font-size: 17px;
  }

  .Toastify__toast {
    font-family: var(--font-regular); /* Change the font-family */
    font-size: 16px; /* Change the font size */
    color: #000;
  }
`;

const Error = styled.div`
  color: #df2323;
  margin-top: -10px;
  margin-bottom: 10px;
  font-size: 14px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 10px;
  background: #ddd;
  // border-radius: 5px;
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => props.progress}%;
    background: #e61717;
    // border-radius: 5px;
    transition: width 0.3s ease;
  }
`;

const ProgressBar2 = styled.div<{ progress: number }>`
  height: 10px;
  background: #ddd;
  margin-bottom: 1rem;
  position: relative;
  margin-top: -2px;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => props.progress}%;
    background: #e61717;
    transition: width 0.3s ease;
  }
`;
const Button = styled.button`
  display: block;
  width: 100%;
  margin-top: 1rem;
  padding: 0.75rem;
  font-size: 1rem;
  border: none;
  background: #e61717;
  color: white;
  font-family: var(--font-normal);

  border-radius: 4px;
  cursor: pointer;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  margin-bottom: 1rem;
  font-family: var(--font-normal);
  border: 1px solid #ddd;
  border-radius: 8px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  /* Remove spinner arrows in Chrome, Safari, Edge, and Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Remove spinner arrows in Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }

  &:focus,
  &:active {
    border-color: #e61717;
    outline: none;
  }
`;

const SelectableCard = styled.button<{ selected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ selected }) => (selected ? "#ffeaea" : "#f8f9fa")};
  border: ${({ selected }) =>
    selected ? "1px solid #e61717" : "1px solid #ddd"};
  color: #333;
  width: 100%;
  font-family: var(--font-normal);
  font-size: 15px;
  padding: 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  text-align: center;

  &:hover {
    background: ${({ selected }) => (selected ? "#ffd6d6" : "#e2e6ea")};
  }
`;

const AutocompleteContainer = styled.div`
  position: relative;
`;

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ccc;
  max-height: 200px;
  overflow-y: auto;
  border-radius: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 10;

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 2px; /* Set the width of the scrollbar */
  }

  ::-webkit-scrollbar-track {
    background-color: #f1f1f1; /* Optional: Background color of the track */
  }

  ::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: #555;
  }
`;

const DropdownItem = styled.li`
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const Title = styled.h3`
  font-size: 24px;
  margin-bottom: 10px;
`;

const PhoneNumber = styled.p`
  color: #333;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  cursor: pointer;
`;

const EditText = styled.span`
  color: #007bff;
  font-size: 14px;
  margin-left: 8px;
  text-decoration: underline;
`;

const OTPWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 20px 0;
`;

const OTPInput = styled.input`
  width: 40px;
  height: 50px;
  font-size: 24px;
  text-align: center;
  border: 2px solid #ccc;
  border-radius: 8px;
  outline: none;
  transition: border 0.3s;

  &:focus {
    border-color: #e61717;
  }
`;

const ResendWrapper = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const TimerText = styled.span`
  color: #666;
  font-size: 16px;
`;

const ResendText = styled.div<{ disabled?: boolean }>`
  margin-top: 6px;
  color: ${({ disabled }) => (disabled ? "#999" : "#e61717")};
  text-decoration: underline;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
`;

const CustomToast = styled.div`
  font-family: "Courier New", Courier, monospace;
  font-size: 18px;
  color: #fff;
  padding: 10px;
`;
